import React, { useState, useMemo } from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  CHART_COLORS,
  formatCompactNumber,
  MobileLegend,
  getResponsiveChartConfig,
  DesktopLegend,
} from "../../utils/chartUtils";

/**
 * DualAxisBarChart Component
 * Shows two metrics with independent Y-axes (e.g., Reach & Engagement)
 * 
 * @param {Array} data - Array of data points with structure:
 *   [{ label: "Jan 2025", metric1: 100000, metric2: 2000 }, ...]
 * 
 * @param {Object} config - Chart configuration:
 *   - metric1Key: string - Key for first metric
 *   - metric2Key: string - Key for second metric
 *   - metric1Name: string - Display name for first metric
 *   - metric2Name: string - Display name for second metric
 *   - metric1Color: string - Color for first metric bars
 *   - metric2Color: string - Color for second metric bars
 *   - metric1Formatter: function - Custom formatter for metric 1
 *   - metric2Formatter: function - Custom formatter for metric 2
 *   - labelKey: string - Key for x-axis labels (default: "label")
 */
export const DualAxisBarChart = ({
  data,
  config = {},
  title = null,
  showLegend = true,
}) => {
  const [showMobileLegend, setShowMobileLegend] = useState(false);

  // Default config values
  const {
    metric1Key = "metric1",
    metric2Key = "metric2",
    metric1Name = "Metric 1",
    metric2Name = "Metric 2",
    metric1Color = CHART_COLORS.primary,
    metric2Color = CHART_COLORS.secondary,
    metric1Formatter = (v) => formatCompactNumber(v),
    metric2Formatter = (v) => formatCompactNumber(v),
    labelKey = "label",
  } = config;

  // Validate data
  const chartData = useMemo(() => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    return data;
  }, [data]);

  // Get responsive config
  const responsiveConfig = getResponsiveChartConfig();

  // Build legend items
  const legendItems = useMemo(() => [
    { color: metric1Color, label: metric1Name, type: "bar" },
    { color: metric2Color, label: metric2Name, type: "bar" },
  ], [metric1Color, metric1Name, metric2Color, metric2Name]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) {
      return null;
    }

    return (
      <div
        style={{
          padding: "12px",
          background: "white",
          border: `1px solid ${CHART_COLORS.tooltipBorder}`,
          borderRadius: "5px",
          fontSize: "14px",
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "8px" }}>{label}</div>
        {payload.map((item, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "4px",
            }}
          >
            <span style={{ color: item.fill, fontWeight: "500" }}>
              {item.name}:
            </span>
            <span style={{ fontWeight: "600" }}>
              {item.dataKey === metric1Key 
                ? metric1Formatter(item.value)
                : metric2Formatter(item.value)
              }
            </span>
          </div>
        ))}
      </div>
    );
  };

  // Empty state
  if (!chartData || chartData.length === 0) {
    return (
      <div className="h-64 sm:h-96 flex items-center justify-center bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
        <div className="text-center">
          <div className="text-4xl mb-2 text-gray-400">📊</div>
          <div className="text-gray-500 font-medium text-sm sm:text-base">
            No data available
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      {/* Optional Title */}
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      )}

      {/* Mobile Legend */}
      {showLegend && (
        <MobileLegend 
          legendItems={legendItems}
          showLegend={showMobileLegend} 
          onToggle={() => setShowMobileLegend(!showMobileLegend)} 
        />
      )}

      {/* Chart Container */}
      <div className="w-full h-80 sm:h-96">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ ...responsiveConfig.margin, right: 60 }}
            barCategoryGap={responsiveConfig.barCategoryGap}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={CHART_COLORS.gridLines}
              vertical={false}
            />

            {/* X-AXIS */}
            <XAxis
              dataKey={labelKey}
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: responsiveConfig.xAxisConfig.fontSize,
                fill: CHART_COLORS.axisText,
              }}
              angle={responsiveConfig.xAxisConfig.angle}
              textAnchor={responsiveConfig.xAxisConfig.textAnchor}
              height={responsiveConfig.xAxisConfig.height}
            />

            {/* LEFT Y-AXIS for Metric 1 */}
            <YAxis
              yAxisId="left"
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: responsiveConfig.yAxisConfig.fontSize,
                fill: metric1Color,
              }}
              tickFormatter={metric1Formatter}
              domain={[0, "auto"]}
              width={responsiveConfig.yAxisConfig.width}
              label={{
                value: metric1Name,
                angle: -90,
                position: "insideLeft",
                style: { fontSize: 12, fill: metric1Color },
              }}
            />

            {/* RIGHT Y-AXIS for Metric 2 */}
            <YAxis
              yAxisId="right"
              orientation="right"
              axisLine={false}
              tickLine={false}
              tick={{
                fontSize: responsiveConfig.yAxisConfig.fontSize,
                fill: metric2Color,
              }}
              tickFormatter={metric2Formatter}
              domain={[0, "auto"]}
              width={60}
              label={{
                value: metric2Name,
                angle: 90,
                position: "insideRight",
                style: { fontSize: 12, fill: metric2Color },
              }}
            />

            {/* TOOLTIP */}
            <Tooltip
              content={<CustomTooltip />}
              cursor={{ fill: "rgba(4, 32, 43, 0.1)" }}
            />

            {/* DESKTOP LEGEND */}
            {showLegend && (
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{
                  paddingTop: "20px",
                  fontSize: "14px",
                  display: typeof window !== 'undefined' && window.innerWidth < 640 ? 'none' : 'block',
                }}
                content={() => <DesktopLegend legendItems={legendItems} />}
              />
            )}

            {/* METRIC 1 BAR (Left axis) */}
            <Bar
              yAxisId="left"
              dataKey={metric1Key}
              fill={metric1Color}
              name={metric1Name}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />

            {/* METRIC 2 BAR (Right axis) */}
            <Bar
              yAxisId="right"
              dataKey={metric2Key}
              fill={metric2Color}
              name={metric2Name}
              radius={[4, 4, 0, 0]}
              maxBarSize={40}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DualAxisBarChart;